import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import {
  FiBriefcase,
  FiCheckCircle,
  FiDollarSign,
  FiDownload,
  FiFileText,
  FiFolder,
  FiSend,
  FiUploadCloud,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import {
  PROPOSAL_FREQUENCY_OPTIONS,
  computeProposalTotal,
  formatProposalTerms,
  rowsFromProposalTerms,
  toDateInputValue,
} from '../utils/proposalUtils';
import { mergeTermRows, rowsFromClientTerms, dropServiceTerms, termRowsFromCatalog } from '../../ProfilePages/components/quoteCatalog';
import { fetchCatalogServices } from '../../../utils/catalogPicks';
import QuoteTermsEditor from '../../ProfilePages/components/QuoteTermsEditor';
import ProposalServices, {
  lineFromPick,
  serviceTaskTotal,
  serviceTypeFromLines,
} from './ProposalServices';
import '../styles/CreateProposalModal.css';

const FIND_LEADS = `
  query FindLeadsForProposal($page: Float!, $limit: Float!) {
    findAllBooking(page: $page, limit: $limit, includeSignUrls: false) {
      data {
        id
        fullName
        email
        phone
        address
        bookingFrequency
      }
    }
  }
`;

const FIND_ONE_LEAD = `
  query FindOneLeadForProposal($id: String!) {
    findOneBooking(id: $id, includeFileUrls: false) {
      id
      fullName
      email
      phone
      address
      bookingFrequency
    }
  }
`;

const PROPOSAL_FIELDS = `
  id
  proposalNumber
  title
  bookingId
  projectId
  clientName
  clientEmail
  companyName
  siteAddress
  contactPerson
  phoneNumber
  value
  serviceCost
  gstAmount
  additionalCharges
  discount
  status
  serviceType
  frequency
  siteSize
  staffRequired
  scopeOfWork
  services
  terms
  version
  proposalDate
  expectedStartDate
  expiresAt
  createdAt
`;

const NEXT_PROPOSAL_NUMBER = `
  query NextProposalNumber {
    nextProposalNumber
  }
`;

const FETCH_PROPOSAL = `
  query FindOneProposal($id: String!) {
    findOneProposal(id: $id) {
      ${PROPOSAL_FIELDS}
    }
  }
`;

const CREATE_PROPOSAL = `
  mutation CreateProposal($input: CreateProposalInput!) {
    createProposal(input: $input) {
      id
      proposalNumber
      status
    }
  }
`;

const UPDATE_PROPOSAL = `
  mutation UpdateProposal($input: UpdateProposalInput!) {
    updateProposal(input: $input) {
      id
      proposalNumber
      status
      value
    }
  }
`;

function numField(value) {
  if (value == null || value === '') return '';
  return String(value);
}

function leadLabel(lead) {
  if (!lead) return '';
  const name = String(lead.fullName || '').trim() || 'Untitled lead';
  const email = String(lead.email || '').trim();
  return email ? `${name} — ${email}` : name;
}

function matchFrequency(value) {
  const raw = String(value || '')
    .split('|')[0]
    .trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower === 'one-off' || lower === 'one off' || lower === 'oneoff') {
    return 'One-time';
  }
  return (
    PROPOSAL_FREQUENCY_OPTIONS.find((option) => option.toLowerCase() === lower) ||
    raw
  );
}

function clientFromLead(lead) {
  const name = String(lead?.fullName || '').trim();
  const address = String(lead?.address || '').trim();
  return {
    clientName: name,
    clientEmail: String(lead?.email || '').trim(),
    phoneNumber: String(lead?.phone || '').trim(),
    siteAddress: address,
    contactPerson: name,
    frequency: matchFrequency(lead?.bookingFrequency),
  };
}

function buildEmptyForm(prefill) {
  return {
    proposalDate: toDateInputValue(new Date()),
    title: prefill?.title || '',
    expectedStartDate: '',
    expiresAt: '',
    bookingId: prefill?.bookingId || '',
    projectId: prefill?.projectId || '',
    clientName: prefill?.clientName || '',
    clientEmail: prefill?.clientEmail || '',
    companyName: prefill?.companyName || '',
    siteAddress: prefill?.siteAddress || '',
    contactPerson: prefill?.contactPerson || '',
    phoneNumber: prefill?.phoneNumber || '',
    serviceType: '',
    frequency: '',
    siteSize: prefill?.siteSize || '',
    staffRequired: prefill?.staffRequired || '',
    serviceCost: '',
    gstAmount: '',
    additionalCharges: '',
    discount: '0',
    scopeOfWork: prefill?.scopeOfWork || '',
    services: [],
    termRows: [],
    terms: '',
  };
}

function termsFromProposal(proposal) {
  const services = Array.isArray(proposal?.services) ? proposal.services : [];
  const fromServices = services.reduce(
    (rows, line) => mergeTermRows(rows, line.sourceTerms || []),
    [],
  );
  if (fromServices.length) return fromServices;
  return rowsFromProposalTerms(proposal?.terms) || rowsFromClientTerms(proposal?.terms);
}

function buildFormFromProposal(proposal) {
  return {
    proposalDate: toDateInputValue(proposal.proposalDate || proposal.createdAt),
    title: proposal.title || '',
    expectedStartDate: toDateInputValue(proposal.expectedStartDate),
    expiresAt: toDateInputValue(proposal.expiresAt),
    bookingId: proposal.bookingId || '',
    projectId: proposal.projectId || '',
    clientName: proposal.clientName || '',
    clientEmail: proposal.clientEmail || '',
    companyName: proposal.companyName || '',
    siteAddress: proposal.siteAddress || '',
    contactPerson: proposal.contactPerson || '',
    phoneNumber: proposal.phoneNumber || '',
    serviceType: proposal.serviceType || '',
    frequency: proposal.frequency || '',
    siteSize: proposal.siteSize || '',
    staffRequired: numField(proposal.staffRequired),
    serviceCost: numField(proposal.serviceCost),
    gstAmount: numField(proposal.gstAmount),
    additionalCharges: numField(proposal.additionalCharges),
    discount: numField(proposal.discount ?? 0),
    scopeOfWork: proposal.scopeOfWork || '',
    services: Array.isArray(proposal.services) ? proposal.services : [],
    termRows: termsFromProposal(proposal),
    terms: proposal.terms || '',
  };
}

function FieldLabel({ children, required }) {
  return (
    <span className="prop-new-label">
      {children}
      {required ? <span className="prop-new-required">*</span> : null}
    </span>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="prop-new-section-head">
      <Icon size={15} aria-hidden />
      <span>{title}</span>
    </div>
  );
}

export default function CreateProposalModal({
  onClose,
  onCreated,
  onSaved,
  prefill,
  proposalId,
}) {
  const isEdit = Boolean(proposalId);
  const [form, setForm] = useState(() => buildEmptyForm(prefill));
  const [proposalNumber, setProposalNumber] = useState('');
  const [currentStatus, setCurrentStatus] = useState('DRAFT');
  const [loading, setLoading] = useState(isEdit);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadQuery, setLeadQuery] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(true);
  const fileInputRef = useRef(null);

  const hasCrmPrefill = !isEdit && Boolean(
    prefill?.bookingId ||
      prefill?.clientName ||
      prefill?.clientEmail ||
      prefill?.companyName,
  );

  const totalValue = useMemo(
    () =>
      computeProposalTotal({
        serviceCost: form.serviceCost,
        gstAmount: form.gstAmount,
        additionalCharges: form.additionalCharges,
        discount: form.discount,
      }),
    [form.serviceCost, form.gstAmount, form.additionalCharges, form.discount],
  );

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (isEdit) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await graphqlRequest(NEXT_PROPOSAL_NUMBER);
        if (!cancelled) setProposalNumber(res.nextProposalNumber || '');
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  useEffect(() => {
    if (!proposalId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await graphqlRequest(FETCH_PROPOSAL, { id: proposalId });
        const proposal = res.findOneProposal;
        if (cancelled || !proposal) return;
        const next = buildFormFromProposal(proposal);
        const structured = (next.termRows || []).some((term) =>
          (term.sources || []).some((source) => source?.catalogId),
        );
        const ids = [...new Set(
          (next.services || []).map((line) => line.catalogServiceId).filter(Boolean),
        )];
        if (!structured && ids.length) {
          try {
            const list = await fetchCatalogServices();
            const idSet = new Set(ids);
            const catalogRows = (list || [])
              .filter((service) => idSet.has(service.id))
              .reduce((acc, service) => mergeTermRows(acc, termRowsFromCatalog(service)), []);
            if (catalogRows.length) {
              next.termRows = catalogRows;
              next.terms = formatProposalTerms(catalogRows);
            }
          } catch (err) {
            console.error(err);
          }
        }
        if (cancelled) return;
        setForm(next);
        setProposalNumber(proposal.proposalNumber || '');
        setCurrentStatus(proposal.status || 'DRAFT');
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error(err?.message || 'Failed to load proposal');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const loadLeads = useCallback(async () => {
    try {
      setLoadingLeads(true);
      const res = await graphqlRequest(FIND_LEADS, { page: 1, limit: 500 });
      setLeads(Array.isArray(res?.findAllBooking?.data) ? res.findAllBooking.data : []);
    } catch (err) {
      console.error('Failed to load leads for proposal form:', err);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    const bookingId = form.bookingId;
    if (!bookingId || loadingLeads) return;
    if (leads.some((row) => row.id === bookingId)) return;
    graphqlRequest(FIND_ONE_LEAD, { id: bookingId })
      .then((res) => {
        const lead = res?.findOneBooking;
        if (!lead?.id) return;
        setLeads((prev) =>
          prev.some((row) => row.id === lead.id) ? prev : [lead, ...prev],
        );
      })
      .catch((err) => {
        console.error('Failed to load selected lead:', err);
      });
  }, [form.bookingId, leads, loadingLeads]);

  useEffect(() => {
    if (!form.bookingId || form.clientName.trim()) return;
    const lead = leads.find((row) => row.id === form.bookingId);
    if (!lead) return;
    setForm((prev) => ({ ...prev, ...clientFromLead(lead) }));
  }, [form.bookingId, form.clientName, leads]);

  const filteredLeads = useMemo(() => {
    const term = leadQuery.trim().toLowerCase();
    const rows = !term
      ? leads
      : leads.filter((lead) =>
          `${lead.fullName || ''} ${lead.email || ''} ${lead.phone || ''}`
            .toLowerCase()
            .includes(term),
        );
    if (form.bookingId && !rows.some((lead) => lead.id === form.bookingId)) {
      const selected = leads.find((lead) => lead.id === form.bookingId);
      if (selected) return [selected, ...rows];
    }
    return rows;
  }, [form.bookingId, leadQuery, leads]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const addFiles = useCallback((incoming) => {
    const list = Array.from(incoming || []).filter((f) => f instanceof File);
    if (!list.length) return;
    setFiles((prev) => {
      const merged = [...prev];
      list.forEach((file) => {
        if (file.size > 20 * 1024 * 1024) return;
        merged.push(file);
      });
      return merged;
    });
  }, []);

  const onFileInput = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLeadChange = (e) => {
    const bookingId = e.target.value;
    const lead = leads.find((row) => row.id === bookingId);
    if (!lead) {
      setForm((prev) => ({ ...prev, bookingId: '', projectId: '' }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      bookingId,
      projectId: prev.bookingId === bookingId ? prev.projectId : '',
      ...clientFromLead(lead),
    }));
  };

  const parseOptionalDate = (value) => (value ? new Date(`${value}T12:00:00`) : undefined);

  const buildPayload = (statusOverride) => ({
    title: form.title.trim(),
    bookingId: form.bookingId || undefined,
    // Kept only while the lead is the one it was raised for.
    projectId: form.projectId || null,
    clientName: form.clientName.trim(),
    clientEmail: form.clientEmail.trim() || undefined,
    companyName: form.companyName.trim() || undefined,
    siteAddress: form.siteAddress.trim() || undefined,
    contactPerson: form.contactPerson.trim() || undefined,
    phoneNumber: form.phoneNumber.trim() || undefined,
    serviceCost: form.serviceCost ? Number(form.serviceCost) : 0,
    gstAmount: form.gstAmount ? Number(form.gstAmount) : 0,
    additionalCharges: form.additionalCharges ? Number(form.additionalCharges) : 0,
    discount: form.discount ? Number(form.discount) : 0,
    value: totalValue,
    serviceType: form.serviceType || undefined,
    frequency: form.frequency || undefined,
    siteSize: form.siteSize.trim() || undefined,
    staffRequired: form.staffRequired ? Number(form.staffRequired) : undefined,
    scopeOfWork: form.scopeOfWork.trim() || undefined,
    services: form.services || [],
    terms: form.terms.trim() || undefined,
    proposalDate: parseOptionalDate(form.proposalDate),
    expectedStartDate: parseOptionalDate(form.expectedStartDate),
    expiresAt: parseOptionalDate(form.expiresAt),
    ...(statusOverride ? { status: statusOverride } : {}),
  });

  const validate = () => {
    if (!form.bookingId) return 'Please select a lead.';
    if (!form.title.trim()) return 'Proposal title is required.';
    if (!form.clientName.trim()) return 'Customer name is required.';
    if (!form.clientEmail.trim()) return 'Email address is required.';
    if (!form.siteAddress.trim()) return 'Site address is required.';
    if (!(form.services || []).length && !form.serviceType) return 'Add a service.';
    if (!form.frequency) return 'Frequency is required.';
    return null;
  };

  const handleSubmit = async (statusOverride) => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    try {
      setSaving(true);
      if (isEdit) {
        const res = await graphqlRequest(UPDATE_PROPOSAL, {
          input: { id: proposalId, ...buildPayload(statusOverride) },
        });
        onSaved?.(res.updateProposal);
        if (statusOverride === 'SENT') {
          toast.success(`Proposal sent to ${form.clientEmail.trim()}.`);
        }
      } else {
        const res = await graphqlRequest(CREATE_PROPOSAL, {
          input: buildPayload(statusOverride || 'DRAFT'),
        });
        onCreated?.(res.createProposal);
        if (statusOverride === 'SENT') {
          toast.success(`Proposal sent to ${form.clientEmail.trim()}.`);
        }
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || `Failed to ${isEdit ? 'update' : 'save'} proposal`);
    } finally {
      setSaving(false);
    }
  };

  const canSend =
    !isEdit ||
    currentStatus === 'DRAFT' ||
    currentStatus === 'REVISION' ||
    currentStatus === 'SENT' ||
    currentStatus === 'VIEWED';

  const sendLabel =
    currentStatus === 'SENT' || currentStatus === 'VIEWED'
      ? 'Re-send Proposal'
      : 'Send Proposal';

  return createPortal(
    <div className="prop-new-overlay" onClick={onClose} role="presentation">
      <div
        className="prop-new-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prop-new-title"
      >
        <header className="prop-new-header">
          <div>
            <h2 id="prop-new-title">{isEdit ? 'Edit Proposal' : 'New Proposal'}</h2>
            <p>
              {isEdit
                ? 'Update proposal details and save your changes'
                : 'Create, manage, and send customer proposals'}
            </p>
          </div>
          <button type="button" className="prop-new-close" onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </button>
        </header>

        {loading ? (
          <div className="prop-new-loading">Loading proposal…</div>
        ) : (
          <>
            <div className="prop-new-body">
              <section className="prop-new-section">
                <SectionHeader icon={FiUser} title="CLIENT DETAILS" />
                {hasCrmPrefill ? (
                  <div className="prop-new-crm-banner">
                    <FiCheckCircle size={16} aria-hidden />
                    <span>Customer information auto-filled from CRM</span>
                  </div>
                ) : null}
                <div className="prop-new-grid">
                  <label className="prop-new-field prop-new-field--full">
                    <FieldLabel required>Lead</FieldLabel>
                    <input
                      type="search"
                      value={leadQuery}
                      onChange={(e) => setLeadQuery(e.target.value)}
                      placeholder="Search leads by name, email or phone"
                      disabled={loadingLeads}
                    />
                    <select
                      name="bookingId"
                      value={form.bookingId}
                      onChange={handleLeadChange}
                      disabled={loadingLeads}
                    >
                      <option value="">
                        {loadingLeads
                          ? 'Loading leads…'
                          : filteredLeads.length === 0
                            ? 'No leads found'
                            : 'Select a lead…'}
                      </option>
                      {filteredLeads.map((lead) => (
                        <option key={lead.id} value={lead.id}>
                          {leadLabel(lead)}
                        </option>
                      ))}
                    </select>
                    <span className="prop-new-hint">
                      Selecting a lead fills the client details below.
                    </span>
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel required>Customer Name</FieldLabel>
                    <input name="clientName" value={form.clientName} onChange={onChange} placeholder="Acme Corporation" />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel required>Email Address</FieldLabel>
                    <input
                      name="clientEmail"
                      type="email"
                      value={form.clientEmail}
                      onChange={onChange}
                      placeholder="contact@acmecorp.com"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Company Name</FieldLabel>
                    <input name="companyName" value={form.companyName} onChange={onChange} placeholder="Acme Corp Pty Ltd" />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel required>Site Address</FieldLabel>
                    <input
                      name="siteAddress"
                      value={form.siteAddress}
                      onChange={onChange}
                      placeholder="123 Design Street, Sydney NSW …"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Contact Person</FieldLabel>
                    <input name="contactPerson" value={form.contactPerson} onChange={onChange} placeholder="John Smith" />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Phone Number</FieldLabel>
                    <input name="phoneNumber" value={form.phoneNumber} onChange={onChange} placeholder="+61 400 122 408" />
                  </label>
                </div>
              </section>

              <section className="prop-new-section">
                <SectionHeader icon={FiFileText} title="PROPOSAL INFORMATION" />
                <div className="prop-new-grid">
                  <label className="prop-new-field">
                    <FieldLabel required>Proposal Number</FieldLabel>
                    <input
                      value={proposalNumber || (isEdit ? '—' : 'Generating…')}
                      readOnly
                      className="prop-new-input--readonly"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel required>Proposal Date</FieldLabel>
                    <input type="date" name="proposalDate" value={form.proposalDate} onChange={onChange} />
                  </label>
                  <label className="prop-new-field prop-new-field--full">
                    <FieldLabel required>Proposal Title</FieldLabel>
                    <input
                      name="title"
                      value={form.title}
                      onChange={onChange}
                      placeholder="e.g. Quarterly Office Cleaning Proposal"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Expected Start Date</FieldLabel>
                    <input type="date" name="expectedStartDate" value={form.expectedStartDate} onChange={onChange} />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Proposal Expiry Date</FieldLabel>
                    <input type="date" name="expiresAt" value={form.expiresAt} onChange={onChange} />
                  </label>
                </div>
              </section>

              <section className="prop-new-section">
                <SectionHeader icon={FiBriefcase} title="SERVICE DETAILS" />
                <div className="prop-new-grid">
                  <label className="prop-new-field">
                    <FieldLabel>Service Type</FieldLabel>
                    <input
                      value={form.serviceType || ''}
                      readOnly
                      className="prop-new-input--readonly"
                      placeholder="Filled from the service you add"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel required>Frequency</FieldLabel>
                    <select name="frequency" value={form.frequency} onChange={onChange}>
                      <option value="">Select…</option>
                      {PROPOSAL_FREQUENCY_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Site Size</FieldLabel>
                    <input name="siteSize" value={form.siteSize} onChange={onChange} placeholder="3000 sq ft" />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Estimated Staff Required</FieldLabel>
                    <input
                      name="staffRequired"
                      type="number"
                      min="0"
                      value={form.staffRequired}
                      onChange={onChange}
                      placeholder="4"
                    />
                  </label>
                </div>
                <div className="prop-new-field prop-new-field--full">
                  <ProposalServices
                    lines={form.services}
                    onAdd={(pick) => {
                      setForm((prev) => {
                        if ((prev.services || []).some((line) => line.catalogServiceId && line.catalogServiceId === pick.catalogId)) {
                          return prev;
                        }
                        const services = [...(prev.services || []), lineFromPick(pick)];
                        const termRows = mergeTermRows(prev.termRows, pick.termRows || []);
                        const serviceCost = String(serviceTaskTotal(services));
                        return {
                          ...prev,
                          services,
                          termRows,
                          terms: formatProposalTerms(termRows),
                          serviceType: serviceTypeFromLines(services),
                          serviceCost,
                        };
                      });
                    }}
                    onRemove={(index) => {
                      setForm((prev) => {
                        const removed = (prev.services || [])[index];
                        const services = (prev.services || []).filter((_, i) => i !== index);
                        const termRows = dropServiceTerms(prev.termRows, removed?.catalogServiceId);
                        const serviceCost = services.length ? String(serviceTaskTotal(services)) : '0';
                        return {
                          ...prev,
                          services,
                          termRows,
                          terms: formatProposalTerms(termRows),
                          serviceType: serviceTypeFromLines(services),
                          serviceCost,
                        };
                      });
                    }}
                    onChange={(services) => {
                      const total = serviceTaskTotal(services);
                      setForm((prev) => ({
                        ...prev,
                        services,
                        serviceType: serviceTypeFromLines(services),
                        serviceCost: (services || []).length ? String(total) : prev.serviceCost,
                      }));
                    }}
                  />
                </div>
              </section>

              <section className="prop-new-section">
                <SectionHeader icon={FiDollarSign} title="PROPOSAL PRICING" />
                <div className="prop-new-grid">
                  <label className="prop-new-field">
                    <FieldLabel>Service Cost</FieldLabel>
                    <input
                      name="serviceCost"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.serviceCost}
                      onChange={onChange}
                      placeholder="0.00"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>GST</FieldLabel>
                    <input
                      name="gstAmount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.gstAmount}
                      onChange={onChange}
                      placeholder="0.00"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Additional Charges</FieldLabel>
                    <input
                      name="additionalCharges"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.additionalCharges}
                      onChange={onChange}
                      placeholder="0.00"
                    />
                  </label>
                  <label className="prop-new-field">
                    <FieldLabel>Discount</FieldLabel>
                    <input
                      name="discount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.discount}
                      onChange={onChange}
                      placeholder="0.00"
                    />
                  </label>
                </div>
                <div className="prop-new-total">
                  <span>Total Proposal Value</span>
                  <strong>{formatAUD(totalValue)}</strong>
                </div>
              </section>

              <section className="prop-new-section">
                <SectionHeader icon={FiFileText} title="PROPOSAL NOTES" />
                <label className="prop-new-field prop-new-field--full">
                  <FieldLabel>Scope of Work</FieldLabel>
                  <textarea
                    name="scopeOfWork"
                    rows={4}
                    value={form.scopeOfWork}
                    onChange={onChange}
                    placeholder="Describe cleaning scope…"
                  />
                </label>
                <div className="prop-new-field prop-new-field--full">
                  <QuoteTermsEditor
                    rows={form.termRows}
                    onChange={(termRows) =>
                      setForm((prev) => ({
                        ...prev,
                        termRows,
                        terms: formatProposalTerms(termRows),
                      }))
                    }
                  />
                </div>
              </section>

              <section className="prop-new-section">
                <SectionHeader icon={FiFolder} title="PROPOSAL FILES" />
                <div
                  className={`prop-new-upload${dragOver ? ' prop-new-upload--active' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                >
                  <FiUploadCloud size={28} className="prop-new-upload-icon" aria-hidden />
                  <p>
                    Drag &amp; drop files here, or{' '}
                    <button type="button" className="prop-new-browse" onClick={() => fileInputRef.current?.click()}>
                      Browse
                    </button>
                  </p>
                  <span className="prop-new-upload-hint">PDF, DOCX, images up to 20 MB</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,image/*"
                    className="prop-new-file-input"
                    onChange={onFileInput}
                  />
                </div>
                {files.length > 0 ? (
                  <ul className="prop-new-file-list">
                    {files.map((file, index) => (
                      <li key={`${file.name}-${index}`}>
                        <span>{file.name}</span>
                        <button type="button" onClick={() => removeFile(index)} aria-label={`Remove ${file.name}`}>
                          <FiX size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            </div>

            <footer className="prop-new-footer">
              {isEdit ? (
                <button
                  type="button"
                  className="prop-new-download"
                  onClick={() => toast.info('PDF download coming soon.')}
                >
                  <FiDownload size={16} />
                  Download PDF
                </button>
              ) : (
                <button
                  type="button"
                  className="prop-new-download"
                  onClick={() => toast.info('PDF download will be available after the proposal is saved.')}
                >
                  <FiDownload size={16} />
                  Download PDF
                </button>
              )}
              <div className="prop-new-footer-actions">
                <button type="button" className="prop-new-cancel" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                {isEdit ? (
                  <>
                    <button
                      type="button"
                      className="prop-new-draft"
                      disabled={saving}
                      onClick={() => handleSubmit()}
                    >
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                    {canSend ? (
                      <button
                        type="button"
                        className="prop-new-send"
                        disabled={saving}
                        onClick={() => handleSubmit('SENT')}
                      >
                        {saving ? 'Sending…' : sendLabel}
                        <FiSend size={15} />
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="prop-new-draft"
                      disabled={saving}
                      onClick={() => handleSubmit('DRAFT')}
                    >
                      {saving ? 'Saving…' : 'Save Draft'}
                    </button>
                    <button
                      type="button"
                      className="prop-new-send"
                      disabled={saving}
                      onClick={() => handleSubmit('SENT')}
                    >
                      {saving ? 'Sending…' : 'Send Proposal'}
                      <FiSend size={15} />
                    </button>
                  </>
                )}
              </div>
            </footer>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
